/* ====================================================================================================================
 * EDITOR DI INCISIONE — Genesi Gioielli
 * --------------------------------------------------------------------------------------------------------------------
 * Web Component <engraving-editor> per il tema Maestrooo "Prestige".
 *
 * Cosa fa:
 *  - Mostra l'immagine del prodotto su un <canvas> con una "safe zone" (area incidibile) tratteggiata.
 *  - Permette al cliente di scrivere un testo, scegliere il font, la dimensione, la rotazione e la posizione
 *    (trascinamento + slider), separatamente per ogni lato (Fronte / Retro).
 *  - Alla conferma genera DUE immagini per ogni lato:
 *       (a) "mockup"  = incisione renderizzata sull'immagine del prodotto (anteprima);
 *       (b) "stampa"  = solo il testo su sfondo trasparente, ad alta risoluzione, per la produzione.
 *  - Carica le immagini su Cloudinary (se configurato) e salva tutti i dati come "line item properties"
 *    al momento dell'aggiunta al carrello, senza modificare il flusso AJAX del tema.
 *
 * IMPORTANTE: questo file è isolato dal bundle del tema (assets/theme.js) e non lo modifica in alcun modo.
 * ==================================================================================================================== */

(() => {
  'use strict';

  /* ------------------------------------------------------------------------------------------------------------------
   * STATO GLOBALE
   * Conserviamo lo stato dell'incisione per ciascun form prodotto (chiave = id del form).
   * In questo modo lo stato sopravvive anche se Prestige ri-renderizza il blocco "buy buttons" al cambio variante.
   * ---------------------------------------------------------------------------------------------------------------- */
  const EDITOR_STATE = new Map(); // formId -> spec confermato (vedi buildSpec)
  const addInFlight = new WeakSet(); // form attualmente in aggiunta al carrello (anti doppio-click)
  let submitHandlerInstalled = false;
  let atcTakeoverInstalled = false;

  /* Prefissi delle chiavi line item property.
   * Le chiavi che iniziano con "_" sono NASCOSTE al cliente (carrello/checkout/email) ma VISIBILI a te nell'admin. */
  const PROP_FLAG = 'data-engraving-prop'; // marca gli <input> iniettati così possiamo rimuoverli/riscriverli

  /* ------------------------------------------------------------------------------------------------------------------
   * UTILITY
   * ---------------------------------------------------------------------------------------------------------------- */
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  /** Carica un'immagine in modo asincrono, con crossOrigin per poterla usare nel canvas senza "taint". */
  function loadImage(src) {
    return new Promise((resolve, reject) => {
      if (!src) {
        resolve(null);
        return;
      }
      const img = new Image();
      img.crossOrigin = 'anonymous'; // il CDN di Shopify e Cloudinary inviano gli header CORS necessari
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null); // se l'immagine non carica, proseguiamo senza sfondo (non blocchiamo l'editor)
      img.src = src;
    });
  }

  /** Assicura che un font sia caricato prima di disegnarlo sul canvas (il canvas non innesca il caricamento da solo). */
  async function ensureFontLoaded(family, sizePx, sampleText) {
    if (!document.fonts || !document.fonts.load) return;
    try {
      // family può contenere una lista di fallback ("'MioFont', serif"): per il caricamento usiamo il PRIMO nome,
      // così garantiamo che il font custom sia effettivamente disponibile prima di disegnarlo sul canvas.
      const primary = String(family).split(',')[0].trim();
      await document.fonts.load(`${Math.max(8, Math.round(sizePx))}px ${primary}`, sampleText || 'Ag');
      await document.fonts.ready;
    } catch (e) {
      /* In caso di errore proseguiamo con il fallback di sistema */
    }
  }

  /** Converte un canvas in Blob (Promise). */
  function canvasToBlob(canvas, type = 'image/png', quality = 0.92) {
    return new Promise((resolve, reject) => {
      try {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('toBlob nullo'))), type, quality);
      } catch (e) {
        reject(e); // canvas "taintato" da immagine cross-origin
      }
    });
  }

  /** Converte un data URL (es. foto del cliente) in Blob, ridimensionato al lato lungo massimo. */
  async function dataUrlToScaledBlob(dataUrl, maxDim) {
    const img = await loadImage(dataUrl);
    if (!img) return null;
    let w = img.naturalWidth || 800;
    let h = img.naturalHeight || 800;
    if (Math.max(w, h) > maxDim) {
      const k = maxDim / Math.max(w, h);
      w = Math.round(w * k);
      h = Math.round(h * k);
    }
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    c.getContext('2d').drawImage(img, 0, 0, w, h);
    return canvasToBlob(c, 'image/jpeg', 0.9);
  }

  /**
   * "Pulisce" la foto di una scritta a mano: la converte in tracce NERE nitide su sfondo TRASPARENTE,
   * e la ritaglia al riquadro dell'inchiostro. Funziona al meglio con foto su foglio bianco, ben illuminate.
   * @param {string} dataUrl   immagine caricata dal cliente (data URL)
   * @param {number} threshold soglia 0..1 (più alta = cattura anche tratti più chiari)
   * @returns {Promise<{src:string, w:number, h:number}|null>}
   */
  async function processHandwriting(dataUrl, threshold = 0.55) {
    const img = await loadImage(dataUrl);
    if (!img) return null;
    const MAX = 1400; // ridimensiono per le performance
    let w = img.naturalWidth || 800;
    let h = img.naturalHeight || 800;
    if (Math.max(w, h) > MAX) {
      const k = MAX / Math.max(w, h);
      w = Math.round(w * k);
      h = Math.round(h * k);
    }
    const src = document.createElement('canvas');
    src.width = w;
    src.height = h;
    const sctx = src.getContext('2d');
    sctx.drawImage(img, 0, 0, w, h);

    const data = sctx.getImageData(0, 0, w, h);
    const px = data.data;
    const cut = threshold * 255;
    const band = 26; // transizione morbida ai bordi (anti-alias)
    let minX = w, minY = h, maxX = -1, maxY = -1;
    for (let i = 0; i < px.length; i += 4) {
      const lum = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
      let a;
      if (lum <= cut - band) a = 255;
      else if (lum >= cut + band) a = 0;
      else a = Math.round((255 * (cut + band - lum)) / (2 * band));
      px[i] = 0; px[i + 1] = 0; px[i + 2] = 0; px[i + 3] = a; // inchiostro nero
      if (a > 20) {
        const p = i >> 2;
        const x = p % w;
        const y = (p / w) | 0;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
    sctx.putImageData(data, 0, 0);

    if (maxX < minX || maxY < minY) {
      return { src: src.toDataURL('image/png'), w, h }; // nessun inchiostro rilevato
    }
    const pad = Math.round(Math.max(w, h) * 0.02);
    const cx = Math.max(0, minX - pad);
    const cy = Math.max(0, minY - pad);
    const cw = Math.min(w - 1, maxX + pad) - cx + 1;
    const ch = Math.min(h - 1, maxY + pad) - cy + 1;
    const out = document.createElement('canvas');
    out.width = cw;
    out.height = ch;
    out.getContext('2d').drawImage(src, cx, cy, cw, ch, 0, 0, cw, ch);
    return { src: out.toDataURL('image/png'), w: cw, h: ch };
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * MOTORE DI RENDERING (condiviso tra anteprima a schermo e immagini finali → garantisce coerenza pixel-perfect)
   * Tutte le coordinate sono normalizzate (0..1) rispetto all'immagine di riferimento, così il risultato è
   * indipendente dalla risoluzione dello schermo del cliente.
   * ---------------------------------------------------------------------------------------------------------------- */

  /**
   * Suddivide il testo in righe che stanno dentro la larghezza massima (word wrapping).
   * Se una singola parola è troppo lunga, viene spezzata per carattere.
   */
  function wrapText(ctx, text, maxWidth) {
    const lines = [];
    const paragraphs = String(text).split('\n');

    for (const paragraph of paragraphs) {
      const words = paragraph.split(/\s+/).filter(Boolean);
      if (words.length === 0) {
        lines.push('');
        continue;
      }
      let current = '';
      for (const word of words) {
        const candidate = current ? `${current} ${word}` : word;
        if (ctx.measureText(candidate).width <= maxWidth || !current) {
          // se la parola da sola sfora, la spezziamo per carattere
          if (!current && ctx.measureText(word).width > maxWidth) {
            let chunk = '';
            for (const char of word) {
              if (ctx.measureText(chunk + char).width > maxWidth && chunk) {
                lines.push(chunk);
                chunk = char;
              } else {
                chunk += char;
              }
            }
            current = chunk;
          } else {
            current = candidate;
          }
        } else {
          lines.push(current);
          current = word;
        }
      }
      if (current) lines.push(current);
    }
    return lines;
  }

  /**
   * Disegna l'intera scena di un lato sul contesto canvas fornito.
   * @param {CanvasRenderingContext2D} ctx
   * @param {Object} params  config del lato + valori scelti dal cliente + dimensioni di riferimento
   * @param {Object} opts    { showGuide: bool, withBackground: bool, backgroundImage: HTMLImageElement|null }
   * @returns {Object} layout risolto (righe + geometria) — utile per salvare uno spec deterministico
   */
  function drawScene(ctx, params, opts) {
    const { refW, refH, side, value } = params;
    const showGuide = !!opts.showGuide;
    const withBackground = !!opts.withBackground;
    const bg = opts.backgroundImage || null;

    // 1) sfondo
    ctx.clearRect(0, 0, refW, refH);
    if (withBackground && bg) {
      ctx.drawImage(bg, 0, 0, refW, refH);
    }

    // 2) safe zone in pixel di riferimento
    const sz = {
      x: side.safeZone.x * refW,
      y: side.safeZone.y * refH,
      w: side.safeZone.w * refW,
      h: side.safeZone.h * refH,
    };

    // 3) contenuto + bordo adattivo (stile Pandora: si trascina tutto il blocco)
    const text = (value.text || '').trim();
    const layout = {
      lines: [], fontPx: 0, lineHeight: 0, blockW: 0, blockH: 0,
      center: { x: value.posX, y: value.posY },
    };

    if (value.mode === 'photo' && opts.photoImg) {
      // MODALITÀ FOTO: disegno la scritta a mano "pulita" (nera su trasparente) come blocco
      const imgEl = opts.photoImg;
      const targetW = (value.photoScale || 0.6) * sz.w;
      const ratioImg = (imgEl.naturalHeight || 1) / (imgEl.naturalWidth || 1);
      const targetH = targetW * ratioImg;
      const cx = value.posX * refW;
      const cy = value.posY * refH;

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate((value.rotationDeg * Math.PI) / 180);
      ctx.drawImage(imgEl, -targetW / 2, -targetH / 2, targetW, targetH);
      ctx.restore();
      // il bordo/maniglie sono forniti dall'overlay HTML (riquadro di selezione)

      layout.blockW = targetW;
      layout.blockH = targetH;
      layout.fontPx = Math.min(targetW, targetH) * 0.24; // usato per padding/clamp
    } else if (value.mode !== 'photo' && text) {
      // il testo si mostra SOLO in modalità testo (in "Foto a mano" non deve mai comparire)
      const fontPx = value.sizeFrac * refH;
      const family = side._resolvedFamily;
      const cx = value.posX * refW;
      const cy = value.posY * refH;

      ctx.save();
      ctx.font = `${fontPx}px ${family}`;
      ctx.textAlign = 'center'; // il testo è SEMPRE centrato
      ctx.textBaseline = 'middle';
      ctx.fillStyle = opts.textColor || side.engravingColor || '#1c1c1c';

      // a capo automatico entro la larghezza dell'area incidibile
      const lines = wrapText(ctx, text, sz.w * 0.96);
      const lineHeight = fontPx * 1.18;

      // dimensioni reali del blocco di testo (per bordo adattivo + trascinamento + clamp)
      let maxLineW = 0;
      for (const line of lines) maxLineW = Math.max(maxLineW, ctx.measureText(line).width);
      const blockW = maxLineW;
      const blockH = lines.length * lineHeight;

      ctx.translate(cx, cy);
      ctx.rotate((value.rotationDeg * Math.PI) / 180);

      const startY = -blockH / 2 + lineHeight / 2;
      lines.forEach((line, i) => ctx.fillText(line, 0, startY + i * lineHeight));

      // cursore di scrittura lampeggiante (solo mentre si scrive sulla foto)
      if (showGuide && opts.caret && opts.focused) {
        const lastLine = lines[lines.length - 1] || '';
        const caretX = ctx.measureText(lastLine).width / 2 + fontPx * 0.08;
        const caretY = startY + (lines.length - 1) * lineHeight;
        ctx.setLineDash([]);
        ctx.strokeStyle = 'rgba(0,0,0,0.85)';
        ctx.lineWidth = Math.max(1, fontPx * 0.05);
        ctx.beginPath();
        ctx.moveTo(caretX, caretY - fontPx * 0.42);
        ctx.lineTo(caretX, caretY + fontPx * 0.42);
        ctx.stroke();
      }

      // il bordo/maniglie sono forniti dall'overlay HTML (riquadro di selezione)
      ctx.restore();

      layout.lines = lines;
      layout.fontPx = fontPx;
      layout.lineHeight = lineHeight;
      layout.blockW = blockW;
      layout.blockH = blockH;
    } else if (showGuide) {
      // nessun testo: contorno leggero dell'area + cursore lampeggiante + invito a scrivere
      const cxA = sz.x + sz.w / 2;
      const cyA = sz.y + sz.h / 2;
      const caretH = (value.sizeFrac || 0.045) * refH;
      ctx.save();
      ctx.strokeStyle = 'rgba(0,0,0,0.22)';
      ctx.lineWidth = Math.max(1, refW * 0.002);
      ctx.setLineDash([refW * 0.012, refW * 0.012]);
      ctx.strokeRect(sz.x, sz.y, sz.w, sz.h);
      // cursore lampeggiante (il classico trattino) al centro: fa capire che si può scrivere
      if (opts.caret && value.mode !== 'photo') {
        ctx.setLineDash([]);
        ctx.strokeStyle = 'rgba(0,0,0,0.8)';
        ctx.lineWidth = Math.max(1, caretH * 0.05);
        ctx.beginPath();
        ctx.moveTo(cxA, cyA - caretH * 0.5);
        ctx.lineTo(cxA, cyA + caretH * 0.5);
        ctx.stroke();
      }
      // invito a scrivere, sotto il cursore
      if (opts.placeholder) {
        ctx.setLineDash([]);
        ctx.fillStyle = 'rgba(0,0,0,0.35)';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = `${Math.max(12, Math.round(sz.h * 0.13))}px sans-serif`;
        ctx.fillText(opts.placeholder, cxA, cyA + caretH * 0.95);
      }
      ctx.restore();
    }
    return layout;
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * INIEZIONE DELLE LINE ITEM PROPERTIES NEL FORM PRODOTTO
   * Usiamo un listener in fase di CAPTURE su document: gira PRIMA dell'handler di Prestige (<product-form>),
   * così gli input sono presenti quando il tema costruisce il FormData per /cart/add.js.
   * ---------------------------------------------------------------------------------------------------------------- */
  function installSubmitHandler() {
    if (submitHandlerInstalled) return;
    submitHandlerInstalled = true;
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        // NB: form.id NON è affidabile — un controllo <input name="id"> (la variante) lo "oscura"
        // (HTMLFormElement [LegacyOverrideBuiltIns]). Usiamo sempre getAttribute('id').
        const formId = form.getAttribute('id');
        if (!formId) return;
        const spec = EDITOR_STATE.get(formId);
        if (!spec) return;
        injectHiddenInputs(form, spec);
      },
      true // capture
    );
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * AGGIUNTA AL CARRELLO — "TAKEOVER" (controllo totale)
   * Alcune app (es. EasyFlow) o configurazioni intercettano il tasto "Aggiungi al carrello" e fanno l'aggiunta
   * a modo loro, SCAVALCANDO l'iniezione delle properties. Per essere certi che incisione + supplementi finiscano
   * sempre nel carrello, quando c'è un'incisione confermata prendiamo NOI il controllo (click E submit):
   *   - blocchiamo il flusso nativo (preventDefault + stopImmediatePropagation, in fase di CAPTURE → giriamo per primi);
   *   - aggiungiamo PRIMA il prodotto principale (l'incisione non deve mai fallire per colpa di un supplemento),
   *     POI i supplementi a parte ("best-effort": un loro errore non blocca l'incisione);
   *   - replichiamo gli eventi del tema (variant:add SUL FORM + cart:change SU documentElement) così il cart drawer
   *     si aggiorna e SI APRE; più un fallback che apre il drawer comunque.
   * ---------------------------------------------------------------------------------------------------------------- */
  function installAtcTakeover() {
    if (atcTakeoverInstalled) return;
    atcTakeoverInstalled = true;

    // Trova il form prodotto coinvolto e l'eventuale spec confermato. Ritorna null se non dobbiamo intervenire.
    const resolve = (event) => {
      let form = null;
      if (event.type === 'submit') {
        form = event.target;
        if (!(form instanceof HTMLFormElement)) return null;
      } else {
        const t = event.target;
        if (!t || !t.closest) return null;
        const btn = t.closest('button[type="submit"], input[type="submit"]');
        if (!btn) return null;
        form = btn.form || (btn.closest && btn.closest('form'));
      }
      if (!form) return null;
      // NB: form.id è inaffidabile (un <input name="id"> — la variante — lo "oscura"): uso getAttribute('id').
      const formId = form.getAttribute('id');
      if (!formId) return null;
      const spec = EDITOR_STATE.get(formId);
      if (!spec) return null; // nessuna incisione confermata su questo form → flusso normale
      return { form, spec };
    };

    const handler = (event) => {
      const r = resolve(event);
      if (!r) return;
      // Prendo il controllo: blocco tema/app esterne e gestisco io l'aggiunta.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (addInFlight.has(r.form)) return; // aggiunta già in corso (doppio click) → ignoro
      addInFlight.add(r.form);
      addEngravedToCart(r.form, r.spec)
        .catch((e) => console.warn('[incisione] add fallita (top-level):', e))
        .then(() => addInFlight.delete(r.form));
    };

    // CAPTURE su document: giriamo PRIMA di tema/app. Copriamo sia il click sul bottone sia il submit (es. Invio).
    document.addEventListener('click', handler, true);
    document.addEventListener('submit', handler, true);
  }

  const postCartAdd = (root, body) =>
    fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });

  /** Aggiunge al carrello il prodotto inciso (sempre) + i supplementi (best-effort), poi apre/aggiorna il cart drawer.
      Tutto avvolto in try/finally: lo stato di caricamento e il bottone NON restano mai bloccati. */
  async function addEngravedToCart(form, spec) {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    console.log('[incisione] takeover ATC: inizio aggiunta');
    try {
      // Sezioni da rigenerare: il cart drawer spinge qui dentro il PROPRIO section id (chiave corretta anche live).
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      // Variante selezionata + quantità
      const idEl = form.querySelector('[name="id"]');
      const variantId = idEl ? idEl.value : null;
      let qty = 1;
      const qEl = form.querySelector('[name="quantity"]');
      if (qEl && parseInt(qEl.value, 10) > 0) qty = parseInt(qEl.value, 10);
      if (!variantId) {
        console.warn('[incisione] variante non trovata, annullo aggiunta');
        return;
      }

      // Properties dell'incisione: un errore qui NON deve mai bloccare l'aggiunta del prodotto.
      let mainProps;
      try {
        mainProps = buildCartProps(spec);
      } catch (e) {
        console.warn('[incisione] buildCartProps ha lanciato, uso properties minime:', e);
        mainProps = { _incisione: '1' };
      }

      const onSuccessEl = form.querySelector('[name="on_success"]');
      const onSuccessDo = (onSuccessEl && onSuccessEl.value) || 'force_open_drawer';

      // 1) PRODOTTO PRINCIPALE, DA SOLO. (sections_url come il tema: variants/<id>)
      const mainRes = await postCartAdd(root, {
        items: [{ id: variantId, quantity: qty, properties: mainProps }],
        sections: sectionsToBundle.join(','),
        sections_url: `${root}variants/${variantId}`,
      });
      let mainJson = null;
      try {
        mainJson = await mainRes.json();
      } catch (_) {}
      if (!mainRes.ok) {
        console.warn('[incisione] aggiunta PRINCIPALE fallita', mainRes.status, mainJson);
        const msg = (mainJson && (mainJson.description || mainJson.message)) || 'Impossibile aggiungere al carrello.';
        form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: msg } }));
        return; // errore vero sul prodotto: niente drawer
      }
      console.log('[incisione] aggiunta PRINCIPALE ok');

      // 2) SUPPLEMENTI, separati e best-effort: un loro errore NON blocca l'incisione già aggiunta.
      const fees = (spec.fees || []).filter((f) => f && f.variantId);
      if (fees.length) {
        const feeItems = fees.map((f) => ({
          id: f.variantId,
          quantity: qty,
          properties: { _supplemento_per: spec.groupLabel || 'Incisione personalizzata' },
        }));
        try {
          const feeRes = await postCartAdd(root, { items: feeItems });
          if (!feeRes.ok) {
            let fj = null;
            try { fj = await feeRes.json(); } catch (_) {}
            console.warn('[incisione] SUPPLEMENTI non aggiunti (status ' + feeRes.status + '):', fj, 'variantIds=', fees.map((f) => f.variantId));
          } else {
            console.log('[incisione] supplementi ok');
          }
        } catch (feeErr) {
          console.warn('[incisione] errore rete supplementi (ignoro, principale già aggiunto):', feeErr);
        }
      }

      // 3) carrello "a pagina": vado a /cart (come il tema)
      const tv = window.themeVariables && window.themeVariables.settings;
      if (tv && (tv.cartType === 'page' || tv.pageType === 'cart')) {
        window.location.href = `${root}cart`;
        return;
      }

      // 4) carrello finale + sezioni aggiornate (DOPO i supplementi), con la chiave corretta del drawer
      let cartContent = {};
      try {
        cartContent = await (await fetch(`${root}cart.js`)).json();
      } catch (_) {}
      if (sectionsToBundle.length) {
        try {
          const sres = await fetch(`${root}?sections=${encodeURIComponent(sectionsToBundle.join(','))}`);
          cartContent.sections = await sres.json();
        } catch (_) {
          cartContent.sections = mainJson && mainJson.sections ? mainJson.sections : cartContent.sections;
        }
      } else {
        cartContent.sections = mainJson && mainJson.sections ? mainJson.sections : cartContent.sections;
      }

      // 5) EVENTI DEL TEMA — il drawer ascolta "cart:change" su documentElement (baseEvent:'variant:add'),
      //    NON "variant:add". Mando entrambi, come fa il ProductForm del tema.
      const addedItems = mainJson && mainJson.items ? mainJson.items : [mainJson];
      form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: addedItems, cart: cartContent, onSuccessDo } }));
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', onSuccessDo, cart: cartContent } })
      );
      console.log('[incisione] eventi variant:add + cart:change inviati');

      // 6) FALLBACK robusto: apro il drawer comunque (slegato dal render, così un errore di render non blocca l'apertura)
      //    + chiedo al tema un refresh server-side che ri-risolve da solo la chiave di sezione corretta.
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      setTimeout(() => {
        try {
          const cd = document.querySelector('cart-drawer');
          if (cd && !cd.hasAttribute('open')) {
            if (typeof cd.show === 'function') cd.show();
            else cd.setAttribute('open', '');
            console.log('[incisione] fallback: drawer aperto manualmente');
          } else if (!cd) {
            const opener = document.querySelector('[aria-controls="cart-drawer"]');
            if (opener) opener.click();
          }
        } catch (e) {
          console.warn('[incisione] fallback apertura drawer fallito:', e);
        }
      }, 0);
    } catch (err) {
      console.warn('[incisione] errore inatteso in addEngravedToCart:', err);
    } finally {
      // GARANTITO: barra di caricamento spenta + bottone sbloccato (rimuovo aria-busy come fa il tema).
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      try {
        form.querySelectorAll('[type="submit"]').forEach((b) => b.removeAttribute('aria-busy'));
      } catch (_) {}
      console.log('[incisione] takeover ATC: fine (loading azzerato)');
    }
  }

  /** Costruisce l'oggetto delle line item properties dallo stato confermato (UNICA fonte di verità).
      Usato sia dal "takeover" (items JSON) sia dall'iniezione di input nascosti nel form (fallback). */
  function buildCartProps(spec) {
    const props = {};
    const add = (name, val) => {
      if (val === undefined || val === null || val === '') return;
      props[name] = String(val);
    };

    // marcatore tecnico (sempre presente quando c'è un'incisione)
    add('_incisione', '1');

    for (const side of spec.sides) {
      const L = side.labelIt; // es. "fronte" / "retro"
      // VISIBILE al cliente
      add(`Incisione ${L}`, side.text);
      // NASCOSTI al cliente, VISIBILI a te nell'admin dell'ordine:
      if (side.mode === 'photo') {
        add(`_tipo ${L}`, 'Foto scritta a mano');
        add(`_foto originale ${L}`, side.originalUrl); // foto ORIGINALE caricata dal cliente
      } else {
        add(`_font ${L}`, side.fontLabel);
        add(`_dimensione ${L}`, `${Math.round(side.fontPx)} px`);
      }
      add(`_inclinazione ${L}`, `${side.rotationDeg}°`);
      add(`_posizione ${L}`, `x ${Math.round(side.posX * 100)}% · y ${Math.round(side.posY * 100)}%`);
      add(`_anteprima ${L}`, side.mockupUrl); // miniatura mostrata nel carrello + link nell'ordine
      add(`_stampa ${L}`, side.productionUrl); // immagine "solo incisione" ad alta risoluzione per la stampa
    }

    // JSON completo (per eventuale rigenerazione/controllo) — una sola property tecnica
    add('_incisione_dati', JSON.stringify(spec.compact));
    return props;
  }

  /** Fallback: rimuove eventuali input precedenti e inietta le properties come <input hidden> nel form. */
  function injectHiddenInputs(form, spec) {
    form.querySelectorAll(`[${PROP_FLAG}]`).forEach((el) => el.remove());
    const props = buildCartProps(spec);
    for (const name in props) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.setAttribute(PROP_FLAG, '');
      input.name = `properties[${name}]`;
      input.value = props[name];
      form.appendChild(input);
    }
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * UPLOAD SU CLOUDINARY (upload "unsigned", direttamente dal browser, senza server)
   * Se Cloudinary non è ancora configurato, l'editor funziona comunque: salva i dati testuali ma salta le immagini.
   * ---------------------------------------------------------------------------------------------------------------- */
  async function uploadToCloudinary(blob, cloudinary, publicIdHint) {
    if (!cloudinary || !cloudinary.cloudName || !cloudinary.uploadPreset) return '';
    const url = `https://api.cloudinary.com/v1_1/${cloudinary.cloudName}/image/upload`;
    const fd = new FormData();
    fd.append('file', blob);
    fd.append('upload_preset', cloudinary.uploadPreset);
    if (cloudinary.folder) fd.append('folder', cloudinary.folder);
    if (publicIdHint) fd.append('public_id', publicIdHint);
    const res = await fetch(url, { method: 'POST', body: fd });
    if (!res.ok) throw new Error(`Cloudinary upload fallito (${res.status})`);
    const json = await res.json();
    return json.secure_url || '';
  }

  /* ==================================================================================================================
   * IL COMPONENTE <engraving-editor>
   * ================================================================================================================== */
  class EngravingEditor extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.formId = this.dataset.formId || '';
      this.drawerId = this.dataset.drawerId || '';
      this.config = this.readConfig();

      if (!this.config || !this.config.enabled || !Array.isArray(this.config.sides) || this.config.sides.length === 0) {
        this.innerHTML = '';
        return;
      }

      installSubmitHandler();
      installAtcTakeover();

      // valori correnti per ciascun lato. Il testo parte SEMPRE centrato nell'area incidibile.
      this.values = this.config.sides.map((side) => ({
        mode: 'text', // 'text' | 'photo'
        text: '',
        fontKey: (this.config.fonts[0] || {}).key || 'serif',
        sizeFrac: side.fontSize.default,
        rotationDeg: side.rotation.default,
        posX: side.safeZone.x + side.safeZone.w / 2,
        posY: side.safeZone.y + side.safeZone.h / 2,
        photoOriginal: null, // data URL della foto caricata (originale)
        photo: null, // { src, w, h } versione "pulita"
        photoThreshold: 0.55,
        photoScale: 0.6, // larghezza come frazione dell'area
      }));
      this.photoImgs = {}; // cache <img> della scritta pulita, per lato

      this.activeSide = 0;
      this.images = {}; // cache immagini di sfondo per lato
      this.restoreFromState(); // ripristina un'incisione già confermata (es. dopo un re-render del tema)
      this.buildUI();
      this.bindEvents();
      this.updateSurcharge(); // stato iniziale del totale (di norma "Personalizzazione inclusa")
      this.updateModeFees(); // prezzi sulle opzioni Testo/Foto per il lato iniziale
      this.preloadImages().then(() => this.render());

      // cursore lampeggiante (il classico trattino): invita a scrivere e segue la digitazione
      this._caretOn = true;
      this._caretTimer = setInterval(() => {
        this._caretOn = !this._caretOn;
        if (this.offsetParent !== null) this.render(); // ridisegno solo quando l'editor è visibile
      }, 530);
    }

    /* Se per questo form esiste già un'incisione confermata, ne ripristina i valori e l'etichetta del bottone. */
    restoreFromState() {
      const spec = EDITOR_STATE.get(this.formId);
      if (!spec || !spec.compact || !spec.compact.sides) return;
      this.config.sides.forEach((side, i) => {
        const saved = spec.compact.sides[side.id];
        if (!saved) return;
        if (saved.mode === 'photo') {
          Object.assign(this.values[i], {
            mode: 'photo',
            photoScale: typeof saved.ps === 'number' ? saved.ps : this.values[i].photoScale,
            rotationDeg: typeof saved.r === 'number' ? saved.r : this.values[i].rotationDeg,
            posX: typeof saved.x === 'number' ? saved.x : this.values[i].posX,
            posY: typeof saved.y === 'number' ? saved.y : this.values[i].posY,
            photo: saved.u ? { src: saved.u } : null,
          });
          if (saved.u) {
            loadImage(saved.u).then((img) => {
              if (img) { this.photoImgs[i] = img; this.render(); }
            });
          }
        } else {
          Object.assign(this.values[i], {
            mode: 'text',
            text: saved.t || '',
            fontKey: saved.f || this.values[i].fontKey,
            sizeFrac: typeof saved.s === 'number' ? saved.s : this.values[i].sizeFrac,
            rotationDeg: typeof saved.r === 'number' ? saved.r : this.values[i].rotationDeg,
            posX: typeof saved.x === 'number' ? saved.x : this.values[i].posX,
            posY: typeof saved.y === 'number' ? saved.y : this.values[i].posY,
          });
        }
      });
      // ripristina l'etichetta del bottone "Aggiungi incisione" → "Modifica" dopo il re-init
      requestAnimationFrame(() => this.markConfirmed());
    }

    /* Legge la configurazione JSON iniettata da Liquid (snippet engraving-config.liquid). */
    readConfig() {
      const id = this.dataset.configId;
      const el = id ? document.getElementById(id) : this.querySelector('script[type="application/json"]');
      if (!el) return null;
      try {
        const cfg = JSON.parse(el.textContent);
        // risolviamo per ogni lato la "family" CSS del font di default (verrà ricalcolata al cambio font)
        cfg.fontsByKey = {};
        (cfg.fonts || []).forEach((f) => (cfg.fontsByKey[f.key] = f));
        return cfg;
      } catch (e) {
        console.error('[incisione] Config JSON non valida:', e);
        return null;
      }
    }

    /* Costruisce l'interfaccia (markup) dentro il componente. Le etichette sono in italiano. */
    buildUI() {
      const sides = this.config.sides;
      const fonts = this.config.fonts;

      const tabs =
        sides.length > 1
          ? `<div class="engraving__tabs" role="tablist">
              ${sides
                .map(
                  (s, i) =>
                    `<button type="button" class="engraving__tab ${i === 0 ? 'is-active' : ''}" data-side="${i}" role="tab">${s.label}</button>`
                )
                .join('')}
             </div>`
          : '';

      // cerchietti "Aa" per il selettore font (ognuno mostra l'anteprima nel proprio carattere)
      const swatches = fonts
        .map(
          (f) =>
            `<button type="button" class="engraving__swatch" data-font-swatch="${f.key}" style="font-family: ${f.family}" title="${f.label}" aria-label="${f.label}">Aa</button>`
        )
        .join('');

      // icone professionali (a linea, monocromatiche) per la guida: pollice su / pollice giù
      const ICON_UP = `<svg class="engraving__guide-ico" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>`;
      const ICON_DOWN = `<svg class="engraving__guide-ico" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3z"/><path d="M17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg>`;
      const guideGood = ['Immagini <b>chiare, luminose e nitide</b> (JPG, PNG o BMP)', 'Foto con <b>sfondo chiaro o semplice</b> e tratto ben visibile', 'Vanno bene <b>scritte a mano, firme, disegni o lettere</b>', 'Vanno bene anche <b>foto di persone e/o animali</b>']
        .map((t) => `<li>${ICON_UP}<span>${t}</span></li>`).join('');
      const guideBad = ['Immagini <b>troppo complesse o piene di dettagli</b> (poco leggibili una volta incise)', 'Foto <b>scure, sfocate o con poco contrasto</b>']
        .map((t) => `<li>${ICON_DOWN}<span>${t}</span></li>`).join('');

      // "coach mark" iniziale: verbo giusto per il dispositivo (tocco su mobile, clic su desktop)
      const coarse = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
      const cueText = coarse
        ? this.t('cueTouch', 'Tocca l’immagine per iniziare')
        : this.t('cueClick', 'Clicca sull’immagine per iniziare');

      this.innerHTML = `
        <div class="engraving">
          ${tabs}

          <div class="engraving__modes" role="tablist">
            <button type="button" class="engraving__mode is-active" data-mode="text" aria-label="${this.t('modeText', 'Testo')}">
              <span class="engraving__mode-preview"><span class="engraving__mode-sample">${this.t('modeTextSample', 'Ti amo')}</span></span>
              <span class="engraving__mode-name">${this.t('modeText', 'Testo')}</span>
              <small class="engraving__mode-fee" data-mode-fee="text" hidden></small>
            </button>
            <button type="button" class="engraving__mode" data-mode="photo" aria-label="${this.t('modePhoto', 'Foto o scritte a mano')}">
              <span class="engraving__mode-preview">
                <svg class="engraving__mode-ico" viewBox="0 0 52 32" width="52" height="32" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 22c4-11 8-12 10.5-4.5S18.5 27 22 20s6.5-11 9.5-4 5 9 7.5 4.5"/><path d="M41 12.5c1.4-1.7 3.6-1.7 4.4.2.5 1.1.1 2.4-1 3.6L41 20l-3.4-3.7c-1.1-1.2-1.5-2.5-1-3.6.8-1.9 3-1.9 4.4-.2z"/></svg>
              </span>
              <span class="engraving__mode-name">${this.t('modePhoto', 'Foto o scritte a mano')}</span>
              <small class="engraving__mode-fee" data-mode-fee="photo" hidden></small>
            </button>
          </div>

          <div class="engraving__stage">
            <div class="engraving__canvas-row">
              <div class="engraving__canvas-wrap">
                <canvas class="engraving__canvas" data-canvas></canvas>
                <div class="engraving__selbox" data-selbox hidden>
                  <span class="engraving__handle engraving__handle--tl" data-handle="resize"></span>
                  <span class="engraving__handle engraving__handle--tr" data-handle="resize"></span>
                  <span class="engraving__handle engraving__handle--bl" data-handle="resize"></span>
                  <span class="engraving__handle engraving__handle--br" data-handle="resize"></span>
                  <span class="engraving__rot-handle" data-handle="rotate" title="${this.t('rotation', 'Rotazione')}">
                    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M9.4 2 7 4.4l2.4 2.4"/><path d="M14 10.8V7.6A3.2 3.2 0 0 0 10.8 4.4H7.2"/><rect x="2.1" y="8.5" width="5.4" height="5.4" rx="1.5"/></svg>
                  </span>
                  <button type="button" class="engraving__del-handle" data-handle="delete" title="${this.t('deleteBtn', 'Elimina')}" aria-label="${this.t('deleteBtn', 'Elimina')}">
                    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3 4.5h10M6.5 4.5V3.2A1 1 0 0 1 7.5 2.2h1a1 1 0 0 1 1 1V4.5M5 4.5l.5 8a1 1 0 0 0 1 .9h3a1 1 0 0 0 1-.9l.5-8"/></svg>
                  </button>
                </div>
                <div class="engraving__cue" data-cue hidden aria-hidden="true">${cueText}</div>
              </div>
              <div class="engraving__size" title="${this.t('size', 'Dimensione')}">
                <span class="engraving__size-label" aria-hidden="true">A<small>a</small></span>
                <input class="engraving__range engraving__range--v" type="range" data-size min="0" max="100" value="50" aria-label="${this.t('size', 'Dimensione')}">
                <button type="button" class="engraving__slider-reset" data-size-reset title="${this.t('resetSize', 'Dimensione predefinita')}" aria-label="${this.t('resetSize', 'Dimensione predefinita')}">&#x21BA;</button>
              </div>
            </div>

            <!-- Campo di scrittura visibile (solo modalità testo): chiaro su desktop e mobile -->
            <div class="engraving__write" data-write-field>
              <svg class="engraving__write-ico" viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11.4 2.3l2.3 2.3L6 12.3l-2.9.6.6-2.9z"/><path d="M10.2 3.5l2.3 2.3"/></svg>
              <textarea class="engraving__write-input" data-text rows="1" inputmode="text" enterkeyhint="enter" autocomplete="off" autocapitalize="words" spellcheck="false" placeholder="${this.t('writePlaceholder', 'Scrivi qui la tua incisione')}" aria-label="${this.t('text', 'Testo da incidere')}"></textarea>
            </div>

            <div class="engraving__rotation" title="${this.t('rotation', 'Rotazione')}">
              <button type="button" class="engraving__slider-reset engraving__rot-icon" data-rot-reset title="${this.t('resetRotation', 'Raddrizza')}" aria-label="${this.t('resetRotation', 'Raddrizza')}">&#x21BB;</button>
              <input class="engraving__range" type="range" data-rotation min="0" max="100" value="50" aria-label="${this.t('rotation', 'Rotazione')}">
            </div>

            <p class="engraving__hint">${this.t('tapHint', 'Scrivi nel campo qui sopra. Trascina la scritta sul gioiello per posizionarla.')}</p>
            <span class="engraving__counter" data-counter></span>
          </div>

          <div class="engraving__panel" data-panel="text">
            <span class="engraving__label">${this.t('font', 'Carattere')}</span>
            <div class="engraving__swatches" role="radiogroup" aria-label="${this.t('font', 'Carattere')}">${swatches}</div>
          </div>

          <div class="engraving__panel" data-panel="photo" hidden>
            <div class="engraving__notice">
              <p class="engraving__notice-title">${this.t('photoNoticeTitle', 'ATTENZIONE!')}</p>
              <p class="engraving__notice-intro">${this.t('photoNoticeIntro', 'Per il caricamento di immagini (foto e/o calligrafia) l’anteprima mostrata è puramente indicativa. Prima della produzione, ogni immagine viene controllata e adattata dai nostri grafici per ottenere la migliore resa sul ciondolo; per questo motivo ricordiamo che:')}</p>
              <ul class="engraving__notice-list">
                <li>${this.t('photoNotice1', 'le scritte con grafia originale potrebbero essere ridimensionate, spostate o suddivise su più righe;')}</li>
                <li>${this.t('photoNotice2', 'gli sfondi verranno sempre rimossi;')}</li>
                <li>${this.t('photoNotice3', 'persone, animali o altri soggetti verranno incisi sempre senza sfondo e in fineline.')}</li>
              </ul>
            </div>

            <label class="engraving__dropzone" title="${this.t('uploadBtn', 'Carica una foto')}">
              <span class="engraving__plus" aria-hidden="true">+</span>
              <input type="file" accept="image/png,image/jpeg,image/bmp,image/*" data-file hidden>
            </label>
            <span class="engraving__upload-label" data-upload-label>${this.t('uploadBtn', 'Carica una foto')}</span>
            <p class="engraving__upload-meta">${this.t('uploadMeta', 'Dimensione massima 15 MB · Formati: JPG, PNG, BMP')}</p>

            <div class="engraving__field engraving__quality" data-threshold-field hidden>
              <span class="engraving__label">${this.t('cleanup', 'Migliora la qualità della scritta')}</span>
              <input class="engraving__range" type="range" data-threshold min="25" max="80" value="55">
            </div>

            <details class="engraving__guide">
              <summary class="engraving__guide-summary">
                <span>${this.t('guideTitle', 'Guida al caricamento immagini')}</span>
                <span class="engraving__guide-toggle" aria-hidden="true"></span>
              </summary>
              <div class="engraving__guide-body">
                <p>${this.t('guideIntro', 'Vuoi un’incisione perfetta? Ecco come scegliere l’immagine migliore.')}</p>
                <p class="engraving__guide-h">${this.t('guideGood', 'Consigli')}</p>
                <ul class="engraving__guide-list">${guideGood}</ul>
                <p class="engraving__guide-h">${this.t('guideBad', 'Meglio evitare')}</p>
                <ul class="engraving__guide-list">${guideBad}</ul>
              </div>
            </details>
          </div>

          <p class="engraving__error" data-error hidden></p>

          <div class="engraving__surcharge" data-surcharge hidden></div>

          <div class="engraving__actions">
            <button type="button" class="button button--outline" data-reset>${this.t('reset', 'Reimposta')}</button>
            <button type="button" class="button" data-confirm>${this.t('confirm', 'Conferma incisione')}</button>
          </div>
        </div>
      `;

      this.canvas = this.querySelector('[data-canvas]');
      this.ctx = this.canvas.getContext('2d');
      this.els = {
        text: this.querySelector('[data-text]'),
        counter: this.querySelector('[data-counter]'),
        size: this.querySelector('[data-size]'),
        rotation: this.querySelector('[data-rotation]'),
        swatches: Array.from(this.querySelectorAll('[data-font-swatch]')),
        error: this.querySelector('[data-error]'),
        reset: this.querySelector('[data-reset]'),
        confirm: this.querySelector('[data-confirm]'),
        writeField: this.querySelector('[data-write-field]'),
        sizeReset: this.querySelector('[data-size-reset]'),
        rotReset: this.querySelector('[data-rot-reset]'),
        cue: this.querySelector('[data-cue]'),
        surcharge: this.querySelector('[data-surcharge]'),
        modeFees: {
          text: this.querySelector('[data-mode-fee="text"]'),
          photo: this.querySelector('[data-mode-fee="photo"]'),
        },
        tabs: this.querySelectorAll('[data-side]'),
        modes: Array.from(this.querySelectorAll('[data-mode]')),
        panelText: this.querySelector('[data-panel="text"]'),
        panelPhoto: this.querySelector('[data-panel="photo"]'),
        file: this.querySelector('[data-file]'),
        threshold: this.querySelector('[data-threshold]'),
        thresholdField: this.querySelector('[data-threshold-field]'),
        uploadLabel: this.querySelector('[data-upload-label]'),
        selbox: this.querySelector('[data-selbox]'),
        handles: Array.from(this.querySelectorAll('[data-handle]')),
      };

      this.syncControlsToValues();
    }

    t(key, fallback) {
      const strings = (this.config && this.config.strings) || {};
      return strings[key] || fallback;
    }

    /* Pre-carica le immagini di sfondo di tutti i lati (in base alla variante/metallo selezionato). */
    async preloadImages() {
      await Promise.all(
        this.config.sides.map(async (side, i) => {
          this.images[i] = await loadImage(this.sideImageUrl(i));
        })
      );
    }

    /* Id della variante attualmente selezionata (lo gestisce il variant-picker del tema). */
    currentVariantId() {
      const form = document.getElementById(this.formId);
      const el = form && form.querySelector('[name="id"]');
      return el && el.value ? String(el.value) : null;
    }

    /* Valore dell'opzione "metallo" (es. Oro/Argento) della variante selezionata. */
    currentMetal() {
      const v = (this.config.variants || {})[this.currentVariantId()];
      if (!v || !v.options) return null;
      const names = this.config.optionNames || [];
      const idx = names.indexOf(this.config.metalOptionName || 'Base');
      return idx >= 0 ? v.options[idx] : v.options[0] || null;
    }

    /* Immagine di sfondo del lato, in base al metallo selezionato (Oro/Argento).
       I gioielli hanno UNA foto per metallo, usata sia per il fronte che per il retro
       (cambia solo l'area incidibile, non l'immagine). */
    sideImageUrl(i) {
      const side = this.config.sides[i];
      const metal = this.currentMetal();
      const mi = this.config.metalImages;
      // override esplicito per lato/metallo (opzionale, se in futuro servisse una foto retro dedicata)
      if (metal && mi && mi[metal] && mi[metal][side.id]) return mi[metal][side.id];
      // foto unica della variante selezionata (Oro/Argento), usata per entrambi i lati
      const v = (this.config.variants || {})[this.currentVariantId()];
      if (v && v.image) return v.image;
      return side.image;
    }

    /* Ricarica le immagini quando il cliente cambia variante (Oro/Argento). */
    async reloadImagesForVariant() {
      await this.preloadImages();
      this.render();
    }

    bindEvents() {
      // cambio tab (lato)
      this.els.tabs.forEach((tab) =>
        tab.addEventListener('click', () => {
          this.activeSide = parseInt(tab.dataset.side, 10);
          this.els.tabs.forEach((t) => t.classList.toggle('is-active', t === tab));
          this.syncControlsToValues();
          this.render();
        })
      );

      // testo: campo VISIBILE sotto il gioiello, con anteprima dal vivo sul ciondolo
      this.els.text.addEventListener('input', () => {
        const side = this.config.sides[this.activeSide];
        let v = this.els.text.value; // l'andare a capo manuale è consentito (il render lo rispetta)
        // Limite caratteri opzionale: si applica SOLO se maxChars è un numero positivo. Se null/0 → nessun limite.
        if (side.maxChars && v.length > side.maxChars) v = v.slice(0, side.maxChars);
        this.els.text.value = v;
        this.value.text = v;
        this.autoGrowText();
        this.updateCounter();
        this.updateSurcharge();
        this.render();
      });
      // In <textarea> l'Invio inserisce un a capo e NON invia il form (aggiungi al carrello):
      // l'andare a capo manuale è quindi consentito senza alcun blocco.
      // mostro/nascondo il cursore quando il campo è attivo
      this.els.text.addEventListener('focus', () => { this._focused = true; this.render(); });
      this.els.text.addEventListener('blur', () => { this._focused = false; this.render(); });

      // "torna al default" degli slider
      this.els.sizeReset && this.els.sizeReset.addEventListener('click', () => this.resetSize());
      this.els.rotReset && this.els.rotReset.addEventListener('click', () => this.resetRotation());

      // font (cerchietti "Aa")
      this.els.swatches.forEach((sw) =>
        sw.addEventListener('click', () => {
          this.value.fontKey = sw.dataset.fontSwatch;
          this.els.swatches.forEach((s) => s.classList.toggle('is-active', s === sw));
          this.render();
        })
      );

      // scelta modalità: Testo / Foto a mano
      this.els.modes.forEach((btn) =>
        btn.addEventListener('click', () => {
          this.value.mode = btn.dataset.mode;
          this.syncControlsToValues();
          this.render();
        })
      );

      // caricamento foto della scritta a mano
      this.els.file.addEventListener('change', (e) => {
        const f = e.target.files && e.target.files[0];
        if (f) this.loadPhoto(f);
        e.target.value = ''; // permette di ricaricare lo stesso file
      });
      this.els.threshold.addEventListener('input', () => {
        this.value.photoThreshold = parseInt(this.els.threshold.value, 10) / 100;
        this.paintSlider(this.els.threshold);
        this.reprocessPhoto();
      });

      // slider dimensione: in modalità testo regola il font, in modalità foto la grandezza della scritta
      this.els.size.addEventListener('input', () => {
        const side = this.config.sides[this.activeSide];
        if (this.value.mode === 'photo') {
          this.value.photoScale = this.mapFromSlider(this.els.size.value, 0.2, 1.0);
        } else {
          this.value.sizeFrac = this.mapFromSlider(this.els.size.value, side.fontSize.min, side.fontSize.max);
        }
        this.paintSlider(this.els.size);
        this.render();
      });
      this.els.rotation.addEventListener('input', () => {
        const side = this.config.sides[this.activeSide];
        this.value.rotationDeg = Math.round(this.mapFromSlider(this.els.rotation.value, side.rotation.min, side.rotation.max));
        this.paintSlider(this.els.rotation);
        this.render();
      });
      // (posizione: solo trascinamento sul canvas — niente slider X/Y, come nello stile Pandora)

      // reset / conferma
      this.els.reset.addEventListener('click', () => this.resetActiveSide());
      this.els.confirm.addEventListener('click', () => this.confirm());

      // trascinamento del testo sul canvas (mouse + touch via Pointer Events)
      this.canvas.addEventListener('pointerdown', (e) => this.onPointerDown(e));
      this.canvas.addEventListener('pointermove', (e) => this.onPointerMove(e));
      this.canvas.addEventListener('pointerup', (e) => this.onPointerUp(e));
      this.canvas.addEventListener('pointercancel', (e) => this.onPointerUp(e));

      // maniglie del riquadro di selezione (ridimensiona / ruota / elimina)
      this.els.handles.forEach((h) => {
        const type = h.dataset.handle;
        if (type === 'delete') {
          h.addEventListener('click', (e) => { e.stopPropagation(); this.clearActiveSide(); });
        } else {
          h.addEventListener('pointerdown', (e) => this.onHandleDown(e, type));
        }
      });

      // ridisegna al ridimensionamento (drawer responsive)
      this._resizeObserver = new ResizeObserver(() => this.render());
      this._resizeObserver.observe(this.querySelector('.engraving__canvas-wrap'));

      // cambio variante (Oro/Argento) → mostra il gioiello del metallo giusto
      this._onVariantChange = () => this.reloadImagesForVariant();
      document.addEventListener('variant:change', this._onVariantChange);
      const idEl = document.getElementById(this.formId);
      const idInput = idEl && idEl.querySelector('[name="id"]');
      if (idInput) idInput.addEventListener('change', this._onVariantChange);
    }

    /* getter comodo per i valori del lato attivo */
    get value() {
      return this.values[this.activeSide];
    }

    /* mappa slider 0..100 -> [min,max] e viceversa */
    mapFromSlider(sliderValue, min, max) {
      return min + (parseInt(sliderValue, 10) / 100) * (max - min);
    }
    mapToSlider(value, min, max) {
      if (max === min) return 50;
      return clamp(((value - min) / (max - min)) * 100, 0, 100);
    }

    /* Aggiorna la parte "riempita" di uno slider (variabile CSS --val), in stile Pandora. */
    paintSlider(el) {
      if (!el) return;
      const min = parseFloat(el.min || 0);
      const max = parseFloat(el.max || 100);
      const pct = max > min ? ((parseFloat(el.value) - min) / (max - min)) * 100 : 50;
      el.style.setProperty('--val', pct + '%');
    }

    syncControlsToValues() {
      const side = this.config.sides[this.activeSide];
      const v = this.value;
      const isPhoto = v.mode === 'photo';

      // modalità attiva
      this.els.modes.forEach((b) => b.classList.toggle('is-active', b.dataset.mode === v.mode));
      this.els.panelText.hidden = isPhoto;
      this.els.panelPhoto.hidden = !isPhoto;
      if (this.els.writeField) this.els.writeField.hidden = isPhoto; // campo di scrittura solo in modalità testo

      // testo
      this.els.text.value = v.text;
      // Applica l'attributo maxlength SOLO se esiste un limite positivo; altrimenti rimuovilo (nessun limite).
      if (side.maxChars && side.maxChars > 0) this.els.text.maxLength = side.maxChars;
      else this.els.text.removeAttribute('maxlength');
      this.autoGrowText();
      this.els.swatches.forEach((s) => s.classList.toggle('is-active', s.dataset.fontSwatch === v.fontKey));

      // slider dimensione: significato diverso per modalità
      this.els.size.value = isPhoto
        ? this.mapToSlider(v.photoScale, 0.2, 1.0)
        : this.mapToSlider(v.sizeFrac, side.fontSize.min, side.fontSize.max);
      this.els.rotation.value = this.mapToSlider(v.rotationDeg, side.rotation.min, side.rotation.max);
      this.paintSlider(this.els.size);
      this.paintSlider(this.els.rotation);

      // controlli foto
      this.els.threshold.value = Math.round(v.photoThreshold * 100);
      this.paintSlider(this.els.threshold);
      this.els.thresholdField.hidden = isPhoto ? !v.photo : true;
      this.els.uploadLabel.textContent = v.photo
        ? this.t('changePhoto', 'Cambia foto')
        : this.t('uploadBtn', 'Carica una foto');

      this.updateCounter();
      this.updateSurcharge();
      this.updateModeFees();
    }

    updateCounter() {
      const side = this.config.sides[this.activeSide];
      if (this.value.mode === 'photo') { this.els.counter.textContent = ''; return; }
      const n = this.value.text.length;
      // Con limite: "n / max". Senza limite (maxChars null/0): mostro solo il numero di caratteri scritti.
      this.els.counter.textContent = (side.maxChars && side.maxChars > 0)
        ? `${n} / ${side.maxChars}`
        : `${n}`;
    }

    /* Adatta l'altezza del campo di scrittura al numero di righe (a capo consentiti). */
    autoGrowText() {
      const el = this.els.text;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    }

    /* Legge il file caricato, lo trasforma in scritta pulita e lo posiziona centrato. */
    loadPhoto(file) {
      const MAX_MB = 15;
      if (!/^image\//.test(file.type || '')) {
        this.showError(this.t('errorFileType', 'Carica un’immagine (JPG, PNG o BMP).'));
        return;
      }
      if (file.size > MAX_MB * 1024 * 1024) {
        this.showError(this.t('errorFileSize', 'Immagine troppo grande (max 15 MB).'));
        return;
      }
      this.showError('');
      const reader = new FileReader();
      reader.onload = async () => {
        this.value.photoOriginal = reader.result;
        await this.reprocessPhoto();
        const side = this.config.sides[this.activeSide];
        this.value.posX = side.safeZone.x + side.safeZone.w / 2;
        this.value.posY = side.safeZone.y + side.safeZone.h / 2;
        this.syncControlsToValues();
        this.render();
      };
      reader.readAsDataURL(file);
    }

    /* Ri-applica il "filtro" di pulizia alla foto con la soglia corrente. */
    async reprocessPhoto() {
      if (!this.value.photoOriginal) return;
      const idx = this.activeSide;
      this.setLoading(true);
      try {
        const processed = await processHandwriting(this.value.photoOriginal, this.value.photoThreshold);
        if (processed) {
          this.value.photo = processed;
          this.photoImgs[idx] = await loadImage(processed.src);
        }
      } finally {
        this.setLoading(false);
      }
      this.els.thresholdField.hidden = false;
      this.els.uploadLabel.textContent = this.t('changePhoto', 'Cambia foto');
      this.render();
    }

    /* Dimensioni di riferimento del lato attivo (dimensione naturale immagine, con tetto per le performance). */
    refDims(sideIndex) {
      const MAX = 1600;
      const img = this.images[sideIndex];
      let w = img ? img.naturalWidth : 1000;
      let h = img ? img.naturalHeight : 1000;
      if (Math.max(w, h) > MAX) {
        const k = MAX / Math.max(w, h);
        w = Math.round(w * k);
        h = Math.round(h * k);
      }
      return { w, h };
    }

    /* Prepara i parametri di rendering per il lato attivo. */
    paramsFor(sideIndex) {
      const side = { ...this.config.sides[sideIndex] };
      const font = this.config.fontsByKey[this.values[sideIndex].fontKey] || this.config.fonts[0];
      side._resolvedFamily = font ? font.family : 'serif';
      const dims = this.refDims(sideIndex);
      return { refW: dims.w, refH: dims.h, side, value: this.values[sideIndex], photoImg: this.photoImgs[sideIndex] };
    }

    /* Clamp del CENTRO del blocco: LIBERO su tutta l'immagine del gioiello (nessun limite all'area
       incidibile). Il cliente posiziona dove vuole; lascio solo un piccolo margine perché il centro
       non finisca esattamente sul bordo dell'immagine. */
    clampCenter(cxN, cyN) {
      const m = 0.02;
      return {
        x: clamp(cxN, m, 1 - m),
        y: clamp(cyN, m, 1 - m),
      };
    }

    /* ----- Rendering a schermo ----- */
    async render() {
      const params = this.paramsFor(this.activeSide);
      const wrap = this.querySelector('.engraving__canvas-wrap');
      const ratio = params.refH / params.refW;
      const dpr = window.devicePixelRatio || 1;

      // larghezza FLUIDA: width 100% così il canvas segue il contenitore e può rimpicciolirsi
      // su mobile (niente larghezza fissa in px, che bloccherebbe il restringimento e farebbe sforare).
      this.canvas.style.width = '100%';
      const cssWidth = Math.max(180, this.canvas.clientWidth || wrap.clientWidth || 300);

      // dimensione CSS (altezza) e risoluzione reale (per nitidezza su schermi retina)
      this.canvas.style.height = `${Math.round(cssWidth * ratio)}px`;
      this.canvas.width = Math.round(cssWidth * dpr);
      this.canvas.height = Math.round(cssWidth * ratio * dpr);

      await ensureFontLoaded(params.side._resolvedFamily, params.value.sizeFrac * params.refH, params.value.text);

      const ctx = this.ctx;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      // scala dal sistema di riferimento (refW x refH) alla dimensione reale del canvas
      const scale = this.canvas.width / params.refW;
      ctx.scale(scale, scale);

      // memorizzo la geometria del blocco per il trascinamento e il clamp
      // Il "coach mark" (fumetto "clicca sull'immagine") appare quando il lato testo è vuoto e non stai scrivendo.
      const showCue = this.value.mode === 'text' && !this.hasActiveContent() && !this._focused;
      // Mentre il fumetto è visibile evito anche la scritta sfumata sul canvas (sarebbe ridondante).
      const placeholder = this.value.mode === 'photo'
        ? this.t('uploadHint', 'Carica una foto della tua scritta')
        : (showCue ? '' : this.t('canvasPlaceholder', 'La tua incisione qui'));
      this._layout = drawScene(ctx, params, {
        showGuide: true,
        withBackground: true,
        backgroundImage: this.images[this.activeSide],
        photoImg: params.photoImg,
        caret: this._caretOn, // fase di lampeggio del cursore
        focused: this._focused,
        placeholder,
      });
      this.updateSelBox();
      if (this.els.cue) this.els.cue.hidden = !showCue;
    }

    /* ----- Riquadro di selezione con maniglie (overlay HTML, stile Pandora) ----- */
    updateSelBox() {
      const sb = this.els.selbox;
      if (!sb) return;
      const L = this._layout;
      const isPhoto = this.value.mode === 'photo';
      const hasContent = isPhoto ? !!this.photoImgs[this.activeSide] : !!(this.value.text || '').trim();
      if (!hasContent || !L || !L.blockW) {
        sb.hidden = true;
        return;
      }
      sb.hidden = false;
      const rect = this.canvas.getBoundingClientRect();
      const wrapRect = sb.parentElement.getBoundingClientRect();
      const dims = this.refDims(this.activeSide);
      const scale = rect.width / dims.w; // da px riferimento a px CSS
      const padX = L.fontPx * 0.5;
      const padY = isPhoto ? L.fontPx * 0.5 : L.fontPx * 0.32;
      const bw = (L.blockW + padX * 2) * scale;
      const bh = (L.blockH + padY * 2) * scale;
      const cx = rect.left - wrapRect.left + this.value.posX * rect.width;
      const cy = rect.top - wrapRect.top + this.value.posY * rect.height;
      sb.style.width = `${bw}px`;
      sb.style.height = `${bh}px`;
      sb.style.left = `${cx}px`;
      sb.style.top = `${cy}px`;
      sb.style.transform = `translate(-50%, -50%) rotate(${this.value.rotationDeg}deg)`;
    }

    blockCenterScreen() {
      const rect = this.canvas.getBoundingClientRect();
      return { x: rect.left + this.value.posX * rect.width, y: rect.top + this.value.posY * rect.height };
    }

    onHandleDown(e, type) {
      e.preventDefault();
      e.stopPropagation();
      const target = e.currentTarget;
      try { target.setPointerCapture(e.pointerId); } catch (_) {}
      const center = this.blockCenterScreen();
      if (type === 'resize') {
        this._handleStartDist = Math.hypot(e.clientX - center.x, e.clientY - center.y) || 1;
        this._handleStartScale = this.value.mode === 'photo' ? this.value.photoScale : this.value.sizeFrac;
      }
      const move = (ev) => this.onHandleMove(ev, type, center);
      const up = (ev) => {
        try { target.releasePointerCapture(ev.pointerId); } catch (_) {}
        target.removeEventListener('pointermove', move);
        target.removeEventListener('pointerup', up);
        target.removeEventListener('pointercancel', up);
      };
      target.addEventListener('pointermove', move);
      target.addEventListener('pointerup', up);
      target.addEventListener('pointercancel', up);
    }

    onHandleMove(e, type, center) {
      const side = this.config.sides[this.activeSide];
      if (type === 'resize') {
        const dist = Math.hypot(e.clientX - center.x, e.clientY - center.y);
        const ratio = dist / this._handleStartDist;
        if (this.value.mode === 'photo') {
          this.value.photoScale = clamp(this._handleStartScale * ratio, 0.2, 1.0);
        } else {
          this.value.sizeFrac = clamp(this._handleStartScale * ratio, side.fontSize.min, side.fontSize.max);
        }
      } else if (type === 'rotate') {
        let deg = (Math.atan2(e.clientY - center.y, e.clientX - center.x) * 180) / Math.PI + 90;
        while (deg > 180) deg -= 360;
        while (deg < -180) deg += 360;
        this.value.rotationDeg = Math.round(clamp(deg, side.rotation.min, side.rotation.max));
      }
      this.syncControlsToValues(); // tiene allineati gli slider
      this.render();
    }

    /* Riporta la DIMENSIONE del lato attivo al valore predefinito (testo → grande di default; foto → 0.6). */
    resetSize() {
      const side = this.config.sides[this.activeSide];
      if (this.value.mode === 'photo') this.value.photoScale = 0.6;
      else this.value.sizeFrac = side.fontSize.default;
      this.syncControlsToValues();
      this.render();
    }

    /* Riporta la ROTAZIONE del lato attivo a "dritto" (valore predefinito, di norma 0°). */
    resetRotation() {
      const side = this.config.sides[this.activeSide];
      this.value.rotationDeg = side.rotation.default;
      this.syncControlsToValues();
      this.render();
    }

    /* Elimina il contenuto del lato attivo (testo o foto). */
    clearActiveSide() {
      if (this.value.mode === 'photo') {
        this.value.photo = null;
        this.value.photoOriginal = null;
        this.photoImgs[this.activeSide] = null;
        this.value.photoScale = 0.6;
      } else {
        this.value.text = '';
      }
      this.syncControlsToValues();
      this.render();
    }

    /* ----- Trascinamento dell'INTERO blocco (stile Pandora) ----- */
    /* C'è qualcosa da spostare sul lato attivo? Testo (modalità testo) o foto caricata (modalità foto). */
    hasActiveContent() {
      return this.value.mode === 'photo'
        ? !!this.photoImgs[this.activeSide]
        : !!(this.value.text && this.value.text.trim());
    }
    pointerNorm(e) {
      const rect = this.canvas.getBoundingClientRect();
      return { x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height };
    }

    /* Il punto è dentro il blocco di testo? (tiene conto della rotazione) */
    insideBlock(p) {
      const L = this._layout;
      if (!L || !L.blockW) return false;
      const dims = this.refDims(this.activeSide);
      const dx = (p.x - this.value.posX) * dims.w;
      const dy = (p.y - this.value.posY) * dims.h;
      const theta = (this.value.rotationDeg * Math.PI) / 180;
      const c = Math.cos(theta);
      const s = Math.sin(theta);
      const lx = dx * c + dy * s; // coordinate locali (annullo la rotazione)
      const ly = -dx * s + dy * c;
      const hw = L.blockW / 2 + L.fontPx * 0.5;
      const hh = L.blockH / 2 + L.fontPx * 0.32;
      return Math.abs(lx) <= hw && Math.abs(ly) <= hh;
    }

    onPointerDown(e) {
      const p = this.pointerNorm(e);
      this._pointerActive = true;
      this._moved = false;
      this._start = p;
      // se afferro DENTRO il blocco mantengo l'offset (trascino in modo naturale)
      this._grab = this.hasActiveContent() && this.insideBlock(p)
        ? { x: p.x - this.value.posX, y: p.y - this.value.posY }
        : { x: 0, y: 0 };
      try { this.canvas.setPointerCapture(e.pointerId); } catch (_) {}
    }
    onPointerMove(e) {
      if (!this._pointerActive) return;
      const p = this.pointerNorm(e);
      if (!this._moved) {
        // distinguo "tocco" da "trascinamento": sotto la soglia è un tocco (per scrivere)
        const dx = (p.x - this._start.x) * (this.canvas.clientWidth || 1);
        const dy = (p.y - this._start.y) * (this.canvas.clientHeight || 1);
        if (Math.hypot(dx, dy) < 5) return;
        if (!this.hasActiveContent()) return; // niente contenuto (testo o foto) = niente da spostare
        this._moved = true;
      }
      const c = this.clampCenter(p.x - this._grab.x, p.y - this._grab.y);
      this.value.posX = c.x;
      this.value.posY = c.y;
      this.render();
    }
    onPointerUp(e) {
      const wasTap = this._pointerActive && !this._moved;
      this._pointerActive = false;
      this._moved = false;
      try { this.canvas.releasePointerCapture(e.pointerId); } catch (_) {}
      // un semplice tocco = scrivi/modifica direttamente lì (solo in modalità testo)
      if (wasTap && this.value.mode === 'text') this.els.text.focus();
    }

    resetActiveSide() {
      const side = this.config.sides[this.activeSide];
      Object.assign(this.value, {
        text: '',
        fontKey: (this.config.fonts[0] || {}).key,
        sizeFrac: side.fontSize.default,
        rotationDeg: side.rotation.default,
        posX: side.safeZone.x + side.safeZone.w / 2,
        posY: side.safeZone.y + side.safeZone.h / 2,
      });
      this.syncControlsToValues();
      this.render();
      this.showError('');
    }

    showError(msg) {
      this.els.error.textContent = msg || '';
      this.els.error.hidden = !msg;
    }

    /* ----- Conferma: validazione → generazione immagini → upload → salvataggio stato ----- */
    async confirm() {
      // Validazione: almeno un lato deve avere un contenuto (testo non vuoto OPPURE una foto caricata).
      const filledSides = [];
      for (let i = 0; i < this.config.sides.length; i++) {
        const v = this.values[i];
        if (v.mode === 'photo') {
          if (v.photo && this.photoImgs[i]) filledSides.push(i);
          continue;
        }
        const text = (v.text || '').trim();
        if (text.length === 0) continue;
        // Blocco "testo troppo lungo" SOLO se esiste un limite positivo; senza limite non si blocca mai.
        const sideMax = this.config.sides[i].maxChars;
        if (sideMax && text.length > sideMax) {
          this.showError(this.t('errorTooLong', 'Il testo supera il numero massimo di caratteri.'));
          return;
        }
        filledSides.push(i);
      }
      if (filledSides.length === 0) {
        this.showError(this.t('errorEmpty', 'Aggiungi un testo o carica una foto prima di confermare.'));
        return;
      }

      this.showError('');
      this.setLoading(true);

      try {
        const specSides = [];
        const compactSides = {};

        for (const i of filledSides) {
          const params = this.paramsFor(i);
          const isPhoto = this.values[i].mode === 'photo';
          if (!isPhoto) {
            await ensureFontLoaded(params.side._resolvedFamily, params.value.sizeFrac * params.refH, params.value.text);
          }

          // canvas di riferimento (alta risoluzione, senza scala né guida)
          const mockupCanvas = document.createElement('canvas');
          mockupCanvas.width = params.refW;
          mockupCanvas.height = params.refH;
          const layout = drawScene(mockupCanvas.getContext('2d'), params, {
            showGuide: false,
            withBackground: true,
            backgroundImage: this.images[i],
            photoImg: params.photoImg,
          });

          const prodCanvas = document.createElement('canvas');
          prodCanvas.width = params.refW;
          prodCanvas.height = params.refH;
          drawScene(prodCanvas.getContext('2d'), params, {
            showGuide: false,
            withBackground: false, // SOLO incisione su trasparente, per la produzione
            backgroundImage: null,
            photoImg: params.photoImg,
            textColor: '#000000',
          });

          // Genero i blob in modo ROBUSTO: se l'immagine del prodotto è cross-origin e "tainta" il canvas,
          // rigenero il mockup SENZA sfondo, così un'anteprima valida esiste comunque.
          let mockupBlob = null;
          try {
            mockupBlob = await canvasToBlob(mockupCanvas);
          } catch (e) {
            console.warn('[incisione] Mockup col prodotto non generabile (immagine cross-origin): uso anteprima senza sfondo.', e);
            const fallback = document.createElement('canvas');
            fallback.width = params.refW;
            fallback.height = params.refH;
            drawScene(fallback.getContext('2d'), params, {
              showGuide: false, withBackground: false, backgroundImage: null, photoImg: params.photoImg, textColor: '#1c1c1c',
            });
            try { mockupBlob = await canvasToBlob(fallback); } catch (_) {}
          }
          let prodBlob = null;
          try { prodBlob = await canvasToBlob(prodCanvas); } catch (e) { console.warn('[incisione] Immagine stampa non generabile.', e); }

          // upload (se Cloudinary è configurato; altrimenti URL vuoti — i dati si salvano comunque)
          let mockupUrl = '';
          let productionUrl = '';
          let originalUrl = '';
          try {
            // suffisso UNIVOCO: evita collisioni tra clienti diversi che incidono lo stesso prodotto
            const uid = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
            const stamp = `${this.formId}-${this.config.sides[i].id}-${uid}`;
            if (mockupBlob) mockupUrl = await uploadToCloudinary(mockupBlob, this.config.cloudinary, `${stamp}-mockup`);
            if (prodBlob) productionUrl = await uploadToCloudinary(prodBlob, this.config.cloudinary, `${stamp}-stampa`);
            // in modalità foto carico anche la FOTO ORIGINALE del cliente (per vederla nell'ordine)
            if (isPhoto && this.values[i].photoOriginal) {
              const origBlob = await dataUrlToScaledBlob(this.values[i].photoOriginal, 2200);
              if (origBlob) originalUrl = await uploadToCloudinary(origBlob, this.config.cloudinary, `${stamp}-originale`);
            }
          } catch (err) {
            console.warn('[incisione] Upload immagini non riuscito, salvo solo i dati.', err);
          }
          // l'anteprima nel carrello usa il mockup; se manca, ripiega sull'immagine di stampa
          const previewUrl = mockupUrl || productionUrl;

          if (isPhoto) {
            specSides.push({
              id: this.config.sides[i].id,
              labelIt: this.config.sides[i].label.toLowerCase(),
              mode: 'photo',
              text: 'Scritta a mano (foto)',
              fontLabel: '',
              rotationDeg: this.values[i].rotationDeg,
              posX: this.values[i].posX,
              posY: this.values[i].posY,
              mockupUrl: previewUrl,
              productionUrl,
              originalUrl,
            });
            compactSides[this.config.sides[i].id] = {
              mode: 'photo',
              ps: +this.values[i].photoScale.toFixed(4),
              r: this.values[i].rotationDeg,
              x: +this.values[i].posX.toFixed(4),
              y: +this.values[i].posY.toFixed(4),
              u: productionUrl,
            };
          } else {
            const font = this.config.fontsByKey[this.values[i].fontKey] || this.config.fonts[0];
            specSides.push({
              id: this.config.sides[i].id,
              labelIt: this.config.sides[i].label.toLowerCase(),
              mode: 'text',
              text: this.values[i].text.trim(),
              fontKey: this.values[i].fontKey,
              fontLabel: font ? font.label : this.values[i].fontKey,
              fontPx: layout.fontPx,
              sizeFrac: this.values[i].sizeFrac,
              rotationDeg: this.values[i].rotationDeg,
              posX: this.values[i].posX,
              posY: this.values[i].posY,
              mockupUrl: previewUrl,
              productionUrl,
            });
            compactSides[this.config.sides[i].id] = {
              mode: 'text',
              t: this.values[i].text.trim(),
              f: this.values[i].fontKey,
              s: +this.values[i].sizeFrac.toFixed(4),
              r: this.values[i].rotationDeg,
              x: +this.values[i].posX.toFixed(4),
              y: +this.values[i].posY.toFixed(4),
              lines: layout.lines,
            };
          }
        }

        // supplementi a pagamento: in base a quali lati hanno contenuto e di che tipo
        const spec = { sides: specSides, compact: { v: 1, sides: compactSides } };
        spec.fees = this.computeFees(filledSides);
        spec.groupLabel = this.t('groupLabel', 'Incisione personalizzata');
        EDITOR_STATE.set(this.formId, spec);
        installAtcTakeover(); // assicura il controllo dell'aggiunta al carrello (idempotente)

        this.markConfirmed();
        this.closeDrawer();
      } catch (err) {
        console.error('[incisione] Errore in conferma:', err);
        this.showError(this.t('errorGeneric', 'Si è verificato un errore. Riprova.'));
      } finally {
        this.setLoading(false);
      }
    }

    /* Determina i supplementi a pagamento in base ai lati compilati e al tipo (testo/foto).
       Regola: fronte testo = GRATIS; fronte foto, retro testo, retro foto = a pagamento. */
    computeFees(filledSides) {
      const feeByKey = {};
      (this.config.fees || []).forEach((f) => {
        if (f && f.variantId) feeByKey[f.key] = f;
      });
      const rule = { 'front:photo': 'frontPhoto', 'back:text': 'backText', 'back:photo': 'backPhoto' };
      const fees = [];
      for (const i of filledSides) {
        const key = rule[`${this.config.sides[i].id}:${this.values[i].mode}`];
        if (key && feeByKey[key]) fees.push({ variantId: feeByKey[key].variantId, label: feeByKey[key].label });
      }
      return fees;
    }

    /* Righe di supplemento in base allo stato ATTUALE (per il totale dal vivo, non ancora confermato). */
    currentFeeLines() {
      const feeByKey = {};
      (this.config.fees || []).forEach((f) => { if (f && f.variantId) feeByKey[f.key] = f; });
      const rule = { 'front:photo': 'frontPhoto', 'back:text': 'backText', 'back:photo': 'backPhoto' };
      const labelByKey = {
        frontPhoto: this.t('feeFrontPhoto', 'Foto fronte'),
        backText: this.t('feeBackText', 'Testo retro'),
        backPhoto: this.t('feeBackPhoto', 'Foto retro'),
      };
      const lines = [];
      this.config.sides.forEach((side, i) => {
        const v = this.values[i];
        const has = v.mode === 'photo' ? !!this.photoImgs[i] : !!(v.text && v.text.trim());
        if (!has) return;
        const key = rule[`${side.id}:${v.mode}`];
        if (key && feeByKey[key]) {
          lines.push({ label: labelByKey[key] || feeByKey[key].label, price: feeByKey[key].price || 0, priceLabel: feeByKey[key].priceLabel || '' });
        }
      });
      return lines;
    }

    /* Formatta un importo in centesimi con la valuta del negozio. */
    formatMoney(cents) {
      const cur = this.config.currency || 'EUR';
      try {
        return new Intl.NumberFormat(this.config.locale || undefined, { style: 'currency', currency: cur }).format(cents / 100);
      } catch (_) {
        return (cents / 100).toFixed(2) + ' ' + cur;
      }
    }

    /* Aggiorna il totale sobrio "Supplemento personalizzazione" vicino a Conferma. */
    updateSurcharge() {
      const el = this.els.surcharge;
      if (!el) return;
      if (!this.config.fees || !this.config.fees.length) { el.hidden = true; return; }
      const totalCents = this.currentFeeLines().reduce((s, l) => s + (l.price || 0), 0);
      el.hidden = false;
      el.innerHTML = totalCents <= 0
        ? `<span class="engraving__surcharge-free">${this.t('included', 'Personalizzazione inclusa')}</span>`
        : `<span class="engraving__surcharge-total">${this.t('surchargeLabel', 'Supplemento personalizzazione')}: <strong>+${this.formatMoney(totalCents)}</strong></span>`;
    }

    /* Mostra il prezzo DI OGNI opzione (Testo/Foto) per il lato attivo, direttamente sui pulsanti. */
    updateModeFees() {
      if (!this.els.modeFees) return;
      const feeByKey = {};
      (this.config.fees || []).forEach((f) => { if (f && f.key) feeByKey[f.key] = f; });
      const hasFees = !!(feeByKey.backText || feeByKey.frontPhoto || feeByKey.backPhoto);
      const side = this.config.sides[this.activeSide];
      const rule = { 'front:photo': 'frontPhoto', 'back:text': 'backText', 'back:photo': 'backPhoto' };
      ['text', 'photo'].forEach((mode) => {
        const span = this.els.modeFees[mode];
        if (!span) return;
        if (!hasFees) { span.textContent = ''; span.hidden = true; return; }
        const key = rule[`${side.id}:${mode}`];
        const fee = key ? feeByKey[key] : null;
        if (!key) {
          // nessun supplemento previsto = incluso (es. testo sul fronte)
          span.textContent = this.t('freeShort', 'incluso');
          span.className = 'engraving__mode-fee engraving__mode-fee--free';
          span.hidden = false;
        } else if (fee) {
          // priceLabel dal Liquid; se manca (liquid non aggiornato) provo a formattare i centesimi; altrimenti nascondo
          const label = fee.priceLabel || (typeof fee.price === 'number' ? this.formatMoney(fee.price) : '');
          span.textContent = label ? '+' + label : '';
          span.className = 'engraving__mode-fee';
          span.hidden = !label;
        } else {
          span.textContent = '';
          span.hidden = true;
        }
      });
    }

    setLoading(on) {
      this.els.confirm.disabled = on;
      this.els.confirm.textContent = on
        ? this.t('loading', 'Attendere…')
        : this.t('confirm', 'Conferma incisione');
    }

    /* Aggiorna l'etichetta del bottone che apre l'editor → fa capire al cliente che l'incisione è impostata. */
    markConfirmed() {
      const trigger = document.querySelector(`[aria-controls="${this.drawerId}"]`);
      if (trigger) {
        trigger.dataset.engravingDone = '1';
        const labelEl = trigger.querySelector('.text-with-icon') || trigger;
        labelEl.textContent = this.t('edit', '✓ Incisione aggiunta — Modifica');
      }
    }

    /* Chiude il drawer del tema (best-effort, compatibile con <x-drawer> di Prestige). */
    closeDrawer() {
      const drawer = this.closest('x-drawer') || document.getElementById(this.drawerId);
      if (!drawer) return;
      if (typeof drawer.hide === 'function') drawer.hide();
      else drawer.removeAttribute('open');
    }
  }

  if (!customElements.get('engraving-editor')) {
    customElements.define('engraving-editor', EngravingEditor);
  }
})();